import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaBars, FaSignOutAlt, FaBell, FaComments, FaCheck, FaUser, FaFileInvoice, FaCalendarAlt, FaClipboardCheck, FaCreditCard, FaFolderOpen } from "react-icons/fa";
import { useSocket } from "../context/SocketContext";
import ThemeCustomizer from "./ThemeCustomizer";
import { useLayoutPortal, getHeaderContent, getPortalIcon } from "../portal";

// Relative time helper
const timeAgo = (date) => {
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
};

const getAdminClientManagementUrl = () => {
  const base = (process.env.REACT_APP_ADMIN_DASHBOARD_URL || "http://localhost:3000").replace(/\/$/, "");
  return `${base}/client`;
};

// Notification type icon + color mapping
const getNotifStyle = (type) => {
  switch (type) {
    case "chat":
      return { icon: FaComments, bg: "bg-blue-100 dark:bg-blue-900/40", color: "text-blue-600 dark:text-blue-400" };
    case "invoice":
      return { icon: FaFileInvoice, bg: "bg-emerald-100 dark:bg-emerald-900/40", color: "text-emerald-600 dark:text-emerald-400" };
    case "meeting":
      return { icon: FaCalendarAlt, bg: "bg-purple-100 dark:bg-purple-900/40", color: "text-purple-600 dark:text-purple-400" };
    case "checklist":
      return { icon: FaClipboardCheck, bg: "bg-amber-100 dark:bg-amber-900/40", color: "text-amber-600 dark:text-amber-400" };
    case "payment":
    case "service_purchase":
      return { icon: FaCreditCard, bg: "bg-pink-100 dark:bg-pink-900/40", color: "text-pink-600 dark:text-pink-400" };
    case "document":
      return { icon: FaFolderOpen, bg: "bg-cyan-100 dark:bg-cyan-900/40", color: "text-cyan-600 dark:text-cyan-400" };
    default:
      return { icon: FaBell, bg: "bg-gray-100 dark:bg-dark-700", color: "text-gray-600 dark:text-gray-400" };
  }
};

const UserHeader = ({ setSidebarOpen, chatboxOpen, setChatboxOpen }) => {
  const { header: headerContent } = useLayoutPortal();
  const headerConfig = getHeaderContent(headerContent);
  const ChatIcon = getPortalIcon(headerConfig.chatButtonIcon || 'FaComments');
  const ProfileMenuIcon = getPortalIcon(headerConfig.profileMenuIcon || 'FaUser');
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const socket = useSocket();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch user data.");
        }

        const data = await response.json();
        if (data.user) {
          setUserData(data.user);
        } else {
          throw new Error("Invalid user data.");
        }
      } catch (error) {
        setError("Session expired. Please log in again.");
        localStorage.removeItem("token");
        navigate("/login");
      }
    };

    fetchUserData();
  }, [navigate]);

  // Fetch notifications from new API
  const fetchNotifications = useCallback(async () => {
    if (!userData?._id) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/notifications/user/${userData._id}`
      );
      if (!response.ok) throw new Error("Failed to fetch notifications.");
      const data = await response.json();
      setNotifications(data);
    } catch (error) {
      console.error(error);
    }
  }, [userData]);

  useEffect(() => {
    if (userData) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 15000);
      return () => clearInterval(interval);
    }
  }, [userData, fetchNotifications]);

  // Socket.io for real-time notification push
  useEffect(() => {
    if (!socket || !userData?._id) return;

    socket.emit('joinRoom', userData._id);

    const handleNewNotification = (notification) => {
      setNotifications(prev => [notification, ...prev]);
    };

    socket.on('newNotification', handleNewNotification);

    return () => {
      socket.off('newNotification', handleNewNotification);
    };
  }, [socket, userData]);

  // Handle logout
  const handleLogout = async () => {
    const isImpersonating = localStorage.getItem("isImpersonating");
    const adminSession = localStorage.getItem("adminSession");

    if (isImpersonating === "true" && adminSession) {
      try {
        const session = JSON.parse(adminSession);
        const token = localStorage.getItem("token");

        if (session.impersonationLogId) {
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/impersonation/end`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ impersonationLogId: session.impersonationLogId }),
          });
        }

        localStorage.removeItem("token");
        localStorage.removeItem("userEmail");
        localStorage.removeItem("userName");
        localStorage.removeItem("isImpersonating");
        localStorage.setItem("adminToken", session.adminToken);
        localStorage.setItem("adminID", session.adminID);
        localStorage.removeItem("adminSession");

        window.location.href = getAdminClientManagementUrl();
      } catch (error) {
        console.error("Error ending impersonation:", error);
        localStorage.removeItem("token");
        navigate("/login");
      }
    } else {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      } catch (error) {
        console.error("Error recording user logout:", error);
      }
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  // Handle notification click - mark as read and navigate/open chat
  const handleNotificationClick = async (notif) => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-read/${notif._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      setNotifications(prev => prev.filter(n => n._id !== notif._id));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }

    setShowDropdown(false);

    // For chat notifications → open chatbox; otherwise navigate
    if (notif.type === 'chat' || notif.type === 'document') {
      if (notif.link?.includes('/chat')) {
        setChatboxOpen(true);
      } else {
        navigate(notif.link || '/dashboard');
      }
    } else if (notif.link) {
      navigate(notif.link);
    }
  };

  // Mark all notifications as read
  const markNotificationsAsRead = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/notifications/mark-all-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({ userId: userData._id }),
      });

      setNotifications([]);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(prev => !prev);
  };

  const getUserInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <header className="sticky top-0 z-30 bg-white dark:bg-dark-800 backdrop-blur-xl border-b-2 border-gray-200 dark:border-dark-700 shadow-sm transition-all duration-300">
      <div className="flex items-center justify-between px-4 py-3 md:px-6">
        {/* Left Section - Menu Button & Welcome */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2.5 rounded-xl bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200 active:scale-95"
            aria-label="Open menu"
          >
            <FaBars className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>

          <div className="hidden md:block">
            <h1 className="text-lg font-semibold text-dark-800 dark:text-white">
              {headerConfig.welcomePrefix}{" "}
              <span className="bg-gradient-to-r from-primary-600 via-primary-500 to-secondary-500 bg-clip-text text-transparent">
                {userData?.name || "User"}
              </span>
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{headerConfig.welcomeSubtitle}</p>
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center gap-2 md:gap-3">
          <ThemeCustomizer />

          {/* Notifications */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={toggleDropdown}
              className="relative p-2.5 rounded-xl bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200 active:scale-95 group"
              aria-label="Notifications"
            >
              <FaBell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              {unreadCount > 0 && (
                <span className="notification-dot">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
              <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 text-xs font-medium text-white bg-dark-800 dark:bg-dark-600 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                {headerConfig.notificationsTooltip}
              </span>
            </button>

            {/* Notification Dropdown */}
            {showDropdown && (
              <div className="dropdown-menu w-[300px] sm:w-96 -right-14 sm:right-0 max-h-[28rem] overflow-hidden flex flex-col z-50">
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between shrink-0">
                  <div>
                    <h3 className="font-semibold text-dark-800 dark:text-white">{headerConfig.notificationsTitle}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {unreadCount > 0 ? `${unreadCount} new` : headerConfig.notificationsEmptyText}
                    </p>
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={markNotificationsAsRead}
                      className="flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-dark-700 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <FaCheck className="w-3 h-3" />
                      {headerConfig.markAllReadText}
                    </button>
                  )}
                </div>

                {/* Notifications List */}
                <div className="overflow-y-auto flex-1">
                  {notifications.length > 0 ? (
                    notifications.slice(0, 20).map((notif) => {
                      const style = getNotifStyle(notif.type);
                      const IconComp = style.icon;
                      return (
                        <div
                          key={notif._id}
                          className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors border-b border-gray-50 dark:border-dark-700 cursor-pointer flex items-start gap-3"
                          onClick={() => handleNotificationClick(notif)}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${style.bg}`}>
                            <IconComp className={`w-4 h-4 ${style.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-dark-700 dark:text-gray-200 truncate">{notif.title}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{notif.message}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{timeAgo(notif.createdAt)}</p>
                          </div>
                          {!notif.isRead && (
                            <span className="w-2.5 h-2.5 rounded-full bg-primary-500 shrink-0 mt-1.5" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-dark-700 flex items-center justify-center">
                        <FaBell className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{headerConfig.notificationsEmptyText}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Chat Button */}
          <button
            id="chat-button-tour"
            onClick={() => setChatboxOpen(!chatboxOpen)}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm
              transition-all duration-200 active:scale-95
              ${chatboxOpen
                ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40'
              }
            `}
            aria-label="Open chat"
          >
            <ChatIcon className="w-4 h-4" />
            <span className="hidden sm:inline">{headerConfig.chatButtonText}</span>
          </button>

          {/* Profile Avatar Dropdown */}
          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={toggleProfileMenu}
              className="flex items-center justify-center transition-all duration-200 hover:opacity-80"
              aria-label="Profile menu"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white font-bold text-sm flex items-center justify-center shadow-md">
                {getUserInitials(userData?.name) || <FaUser className="w-4 h-4" />}
              </div>
            </button>

            {showProfileMenu && (
              <div className="absolute right-0 mt-3 w-56 rounded-xl bg-white dark:bg-dark-800 shadow-xl border-2 border-gray-200 dark:border-dark-700 z-50 overflow-hidden animate-scale-in">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-700/50">
                  <p className="text-sm font-semibold text-dark-800 dark:text-white truncate">{userData?.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{userData?.email}</p>
                </div>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate("/profile");
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                >
                  <ProfileMenuIcon className="w-4 h-4 text-primary-500" />
                  <span className="font-medium">{headerConfig.profileMenuText}</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-t border-gray-200 dark:border-dark-700"
                >
                  <FaSignOutAlt className="w-4 h-4" />
                  <span className="font-medium">{headerConfig.logoutText}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

    </header>
  );
};

export default UserHeader;